import { ArrowRight } from "lucide-react";
import FadeIn from "./FadeIn";
import { C } from "./brandTokens";

interface PricingSectionProps {
  onCTA: () => void;
}

const DARK = "#1B1410";

export default function PricingSection({ onCTA }: PricingSectionProps) {
  return (
    <section
      id="pricing"
      className="py-[140px] px-6 sm:px-10 relative overflow-hidden"
      style={{ background: DARK }}
    >
      {/* Subtle cross motif */}
      <div
        className="absolute opacity-[0.04] hidden md:block"
        style={{ top: "20%", right: "6%", width: 160, height: 160 }}
      >
        <div className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2" style={{ background: C.cream }} />
        <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2" style={{ background: C.cream }} />
      </div>

      <div className="max-w-[620px] mx-auto text-center relative z-10">
        <FadeIn>
          {/* Heading */}
          <h2
            className="font-heading font-semibold"
            style={{
              fontSize: "clamp(36px, 4.5vw, 58px)",
              lineHeight: 1.1,
              color: C.cream,
              marginBottom: 32,
            }}
          >
            Begin free.
            <br />
            <em className="italic" style={{ color: "rgba(139,158,118,0.88)" }}>
              Stay when it's right.
            </em>
          </h2>

          {/* Rule */}
          <div
            className="mx-auto mb-10"
            style={{ width: 40, height: 1, background: "rgba(250,248,243,0.18)" }}
          />

          {/* Two-card plan lockup */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-10 text-left"
            style={{ maxWidth: 560, margin: "0 auto 40px auto" }}
          >
            {/* Explorer — free */}
            <div
              style={{
                background: "rgba(250,248,243,0.04)",
                border: "1px solid rgba(250,248,243,0.10)",
                borderRadius: 6,
                padding: "28px 24px",
              }}
            >
              <div
                className="font-ui"
                style={{
                  fontSize: 11,
                  color: "rgba(250,248,243,0.42)",
                  letterSpacing: "1.4px",
                  marginBottom: 10,
                }}
              >
                EXPLORER
              </div>
              <div
                className="font-heading font-semibold"
                style={{
                  fontSize: 34,
                  color: C.cream,
                  lineHeight: 1,
                  marginBottom: 2,
                }}
              >
                Free
              </div>
              <div
                className="font-ui"
                style={{
                  fontSize: 12,
                  color: "rgba(250,248,243,0.42)",
                  marginBottom: 18,
                }}
              >
                forever
              </div>
              <div
                style={{
                  height: 1,
                  background: "rgba(250,248,243,0.08)",
                  marginBottom: 18,
                }}
              />
              <ul
                className="font-ui space-y-2 list-none p-0 m-0"
                style={{
                  fontSize: 13,
                  color: "rgba(250,248,243,0.62)",
                  lineHeight: 1.65,
                }}
              >
                <li>Unlimited journaling</li>
                <li>The Pastor companion</li>
                <li>Daily verse recommendations</li>
                <li>Streak tracking</li>
                <li>PDF export (single entry)</li>
              </ul>
            </div>

            {/* Devoted — $7/mo */}
            <div
              style={{
                position: "relative",
                background: "rgba(250,248,243,0.06)",
                border: "1px solid rgba(139,158,118,0.35)",
                borderRadius: 6,
                padding: "28px 24px",
              }}
            >
              <span
                className="font-ui"
                style={{
                  position: "absolute",
                  top: -10,
                  right: 16,
                  fontSize: 10,
                  color: C.cream,
                  letterSpacing: "1.2px",
                  background: "rgba(139,158,118,0.25)",
                  border: "1px solid rgba(139,158,118,0.45)",
                  borderRadius: 999,
                  padding: "3px 10px",
                }}
              >
                MOST CHOSEN
              </span>
              <div
                className="font-ui"
                style={{
                  fontSize: 11,
                  color: "rgba(139,158,118,0.85)",
                  letterSpacing: "1.4px",
                  marginBottom: 10,
                }}
              >
                DEVOTED
              </div>
              <div className="flex items-baseline gap-2" style={{ marginBottom: 2 }}>
                <span
                  className="font-heading font-semibold"
                  style={{
                    fontSize: 34,
                    color: C.cream,
                    lineHeight: 1,
                  }}
                >
                  $7
                </span>
                <span
                  className="font-ui"
                  style={{ fontSize: 14, color: "rgba(250,248,243,0.55)" }}
                >
                  /month
                </span>
              </div>
              <div
                className="font-ui"
                style={{
                  fontSize: 12,
                  color: "rgba(250,248,243,0.42)",
                  marginBottom: 18,
                }}
              >
                or $84/year — save $24
              </div>
              <div
                style={{
                  height: 1,
                  background: "rgba(250,248,243,0.08)",
                  marginBottom: 18,
                }}
              />
              <ul
                className="font-ui space-y-2 list-none p-0 m-0"
                style={{
                  fontSize: 13,
                  color: "rgba(250,248,243,0.62)",
                  lineHeight: 1.65,
                }}
              >
                <li style={{ color: "rgba(250,248,243,0.85)" }}>Everything in Explorer</li>
                <li>All three personas — Pastor, Theologian, Spiritual Psychologist</li>
                <li>Closing prayer generation</li>
                <li>Monthly spiritual insights</li>
                <li>Sunday Sermon email</li>
                <li>Full data export (PDF, CSV, JSON)</li>
              </ul>
            </div>
          </div>

          {/* Reassurance line */}
          <p
            className="font-ui"
            style={{
              fontSize: 13,
              color: "rgba(250,248,243,0.42)",
              textAlign: "center",
              marginBottom: 0,
            }}
          >
            All plans start with ten full-access mornings. No credit card required.
          </p>

          {/* CTA */}
          <div className="mt-12 mb-5">
            <button
              onClick={onCTA}
              className="font-ui font-medium border-none rounded-lg inline-flex items-center gap-2 cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
              style={{
                fontSize: 15,
                padding: "14px 38px",
                color: DARK,
                background: C.cream,
                boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
                letterSpacing: "0.3px",
              }}
              onMouseEnter={() => import("@/components/AuthModal")}
            >
              Begin Your Practice <ArrowRight size={15} />
            </button>
          </div>

          <p
            className="font-ui"
            style={{ fontSize: 12, color: "rgba(250,248,243,0.30)" }}
          >
            No credit card &nbsp;·&nbsp; No auto-charge &nbsp;·&nbsp; Cancel anytime &nbsp;·&nbsp; Your journal is always yours
          </p>
        </FadeIn>
      </div>
    </section>
  );
}
